/**
 * FrameBudgetTypes — wire shape for sustained-slowness telemetry (plan 13 §8,
 * 2026-10-07).
 *
 * The freeze sentinel (FreezeEventTypes) records rAF gaps over 150 ms. A game
 * that runs at 35 fps with 80 ms spikes trips it rarely or never, yet feels
 * terrible — DJ's "INSANE perf" golem fight was exactly that, and it left no
 * trace in the domain log. The frame-budget monitor closes the gap: it watches
 * every frame in fixed windows and, when a focused window misses its budget,
 * sends ONE summary row that says what the client was doing:
 *
 *  - frame times (p50/p95/p99/max, frames over 33/50 ms) for the window;
 *  - where the time went — measured ms of work per second by phase (`scene`,
 *    `tween`, `render`, `react`) and the scripts named by Long Animation Frames;
 *  - what was on screen — map, mob/player counts, Pixi display objects by kind,
 *    GPU textures, graphics contexts, heap, DOM nodes.
 *
 * Recorded server-side as the `system` DomainLog event
 * `FRAME_BUDGET_DOMAIN_LOG_EVENT` (JSONL under the stack's domain-log dir), so a
 * report can be read after the browser is closed. Guide: docs/guides/PERF_ANALYSIS.md.
 */

export const FRAME_BUDGET_DOMAIN_LOG_EVENT = 'frame_budget_degraded';

export const FRAME_BUDGET_CONFIG = {
  /** Length of one measurement window, ms. */
  WINDOW_MS: 10_000,
  /** A window needs at least this many focused frames to be judged. */
  MIN_FRAMES: 60,
  /** Degraded when the window's p95 frame time exceeds this (ms)… */
  P95_BUDGET_MS: 25,
  /** …or at least this many frames took longer than 50 ms. */
  MAX_FRAMES_OVER_50: 3,
  /** At most one report per this interval, ms (flood guard). */
  MIN_REPORT_INTERVAL_MS: 30_000,
  /** Frames longer than this are pauses (debugger, sleep, throttling), not slowness. */
  PAUSE_CEILING_MS: 5_000,
} as const;

export interface FrameBudgetFrameStats {
  frames: number;
  fps: number;
  p50Ms: number;
  p95Ms: number;
  p99Ms: number;
  maxMs: number;
  over33: number;
  over50: number;
}

export interface FrameBudgetSceneCounts {
  /** Every display object under the Pixi stage. */
  displayObjects: number;
  graphics: number;
  sprites: number;
  text: number;
  /** Live GPU textures (renderer.texture.managedTextures without holes). */
  gpuTextures: number | null;
  /** GraphicsContexts with GPU data. */
  graphicsContexts: number | null;
}

/** Sent client→server as `netobs:frameBudget` when a window misses its budget. */
export interface FrameBudgetEventWire {
  capturedAt: number;
  windowMs: number;
  route: string;
  levelId?: string;
  focusClass: 'focused' | 'visible-unfocused' | 'hidden' | 'unknown';
  frames: FrameBudgetFrameStats;
  /** Measured ms of work per second by phase, descending (FramePhaseBudget). */
  phaseBudgetMs: { phase: string; msPerSec: number }[];
  /** React commits per second inside the profiled game tree (dev builds). */
  reactCommitsPerSec: number | null;
  /** Long Animation Frames (≥ 50 ms) in the window and their top scripts. */
  longFrames: number;
  loafScripts: { name: string; ms: number }[];
  mobCount: number | null;
  playerCount: number | null;
  scene: FrameBudgetSceneCounts | null;
  heapUsedMB: number | null;
  domNodes: number | null;
}
